/**
 * StatBreakdownModal.jsx — bottom-sheet drill-down for any clicked
 * Scribe/Reader stat tile. Hits
 *   GET /api/admin/users/{user_id}/profile/breakdown?kind=<kind>
 * and renders the returned `items[]` list (title + subtitle + optional
 * cover thumb). The endpoint is merge-group aware, so a linked
 * sub-account sees the same breakdown rows as the primary.
 *
 * Feb 12 2026 — user spec: *"Make the stats clickable. To link and
 * show the break downs"*.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X as XIcon, Loader2, BookOpen, ChevronRight } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TITLES = {
  ebooks:           'Ebooks',
  audiobooks:       'Audiobooks',
  cinematic:        'Cinematic series',
  book_series:      'Book series',
  text_episodes:    'Book-series episodes',
  chapters:         'Audiobook chapters',
  readers:          'Readers',
  followers:        'Followers',
  episode_finishes: 'Episode finishes',
  books_opened:     'Books opened',
  books_completed:  'Books completed',
  listening:        'Listening',
  achievements:     'Achievements',
};

const StatBreakdownModal = ({ userId, kind, open, onOpenChange }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !userId || !kind) return;
    let cancelled = false;
    setLoading(true);
    setErr('');
    setData(null);
    (async () => {
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/admin/users/${userId}/profile/breakdown?kind=${encodeURIComponent(kind)}`,
        );
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setErr(d.detail || `Could not load breakdown (${r.status})`);
          return;
        }
        setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId, kind]);

  if (!open) return null;

  const title = TITLES[kind] || 'Breakdown';
  const items = data?.items || [];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="stat-breakdown-overlay"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl bg-[#0f1117] border border-white/10 max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="stat-breakdown-dialog"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
              Breakdown
            </p>
            <p className="text-base font-bold text-white">
              {title} {data?.count != null && (
                <span className="text-slate-400 font-normal">({data.count})</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="stat-breakdown-close"
            aria-label="Close breakdown"
          >
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="px-5 py-8 flex items-center justify-center text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {err && !loading && (
            <p className="px-5 py-6 text-rose-300 text-sm" data-testid="stat-breakdown-error">
              {err}
            </p>
          )}
          {!loading && !err && items.length === 0 && (
            <div className="px-5 py-10 text-center text-slate-400 text-sm" data-testid="stat-breakdown-empty">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Nothing here yet.
            </div>
          )}
          {!loading && !err && items.length > 0 && (
            <ul className="divide-y divide-white/5" data-testid="stat-breakdown-list">
              {items.map((it, idx) => {
                // Each row deep-links to the relevant page when the backend
                // supplies a `link` (ebook → /reader/:id, scribe series →
                // /series/:id, follower → /writer/:id, etc.). Falls back
                // to a non-interactive row when no link applies.
                const goTo = () => {
                  if (!it.link) return;
                  onOpenChange(false);
                  navigate(it.link);
                };
                const isLinkable = !!it.link;
                const RowTag = isLinkable ? 'button' : 'li';
                const baseCls = "px-5 py-3 flex items-center gap-3 w-full text-left";
                const hoverCls = isLinkable
                  ? " hover:bg-white/5 focus:outline-none focus-visible:bg-white/10 cursor-pointer"
                  : "";
                const inner = (
                  <>
                    {it.cover_url ? (
                      <img
                        src={it.cover_url}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover ring-1 ring-white/10 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/30 to-fuchsia-500/30 grid place-items-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 text-amber-200" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{it.title}</p>
                      {it.subtitle ? (
                        <p className="text-[11px] text-slate-400 truncate">{it.subtitle}</p>
                      ) : null}
                    </div>
                    {typeof it.progress_pct === 'number' && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-200 tabular-nums">
                        {it.progress_pct}%
                      </span>
                    )}
                    {isLinkable && (
                      <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    )}
                  </>
                );
                if (isLinkable) {
                  return (
                    <li key={(it.id || '') + idx} data-testid={`stat-breakdown-item-${idx}`}>
                      <button
                        type="button"
                        onClick={goTo}
                        className={baseCls + hoverCls}
                        data-testid={`stat-breakdown-item-link-${idx}`}
                      >
                        {inner}
                      </button>
                    </li>
                  );
                }
                return (
                  <li
                    key={(it.id || '') + idx}
                    className={baseCls}
                    data-testid={`stat-breakdown-item-${idx}`}
                  >
                    {inner}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatBreakdownModal;
